/**
 * Cadastro na newsletter (rodapé e pop-up) → dataLayer + correspondência
 * avançada da Meta.
 *
 * **Antes:** os dois formulários empurravam `generate_lead` com e-mail,
 * telefone e nome EM TEXTO no dataLayer (spec 2026-08-10, pensando em
 * Enhanced Conversions/Advanced Matching configurados no GTM). Nenhuma tag
 * do GTM-N7CX5NT chegou a ler esses campos — só ficavam expostos a todo
 * script de terceiro que lê o dataLayer.
 *
 * **Agora:**
 *  1. O e-mail vai pra `fbeEventUserEmail` (a mesma chave que o pixel do FBE
 *     grava no checkout) e `syncMetaUserData()` recalcula o cache
 *     `dt_meta_user_data` e re-inicializa o Pixel com o hash — a partir daí
 *     os eventos dessa pessoa (inclusive nas próximas visitas) saem com `em`,
 *     e o `~/sdk/metaCapi` também manda o e-mail pro `vtex.facebook-capi`.
 *  2. `generate_lead` continua sendo empurrado (mesmo nome e `lead_source`),
 *     mas SÓ com hashes, nos nomes que o Google Ads aceita pré-hasheados
 *     (`sha256_email_address`, `sha256_phone_number`) — a porta pra Enhanced
 *     Conversions segue aberta sem dado pessoal em texto.
 *
 * Ordem importa: user data antes do `generate_lead`, pra uma tag de Lead
 * futura no GTM já disparar com o Pixel identificado.
 *
 * Respeita a recusa do banner (`hasCookieConsent`): com recusa, nada vai pro
 * dataLayer nem pro storage. Nunca lança — chamado depois do cadastro já
 * ter dado certo, não pode quebrar a tela de sucesso.
 */
import { FBE_EMAIL_STORAGE_KEY } from "~/sdk/metaCapi";
import { sha256Hex, syncMetaUserData } from "~/sdk/metaUserData";
import { hasCookieConsent } from "~/utils/consent";

export type LeadSource = "newsletter_footer" | "lightbox_popup";

export interface NewsletterLead {
  source: LeadSource;
  email: string;
  phone?: string;
  gender?: "female" | "male" | "undisclosed";
}

/** E-mail como a Meta/Google pedem: trim + minúsculas. */
export function normalizeEmail(email: string): string | null {
  const e = email.trim().toLowerCase();
  return e.indexOf("@") > 0 ? e : null;
}

/**
 * Telefone em E.164 só com dígitos (o formato que Meta e Google pedem antes
 * do hash). O formulário aceita "(48) 99999-0000": 10–11 dígitos = número
 * brasileiro sem DDI → prefixa 55.
 */
export function normalizePhone(phone: string): string | null {
  let digits = phone.replace(/\D/g, "");
  if (digits.length === 10 || digits.length === 11) digits = `55${digits}`;
  return digits.length >= 12 ? digits : null;
}

/** Pura (exceto o hash): o push do dataLayer, sem nenhum dado em texto. */
export async function buildLeadEvent(lead: NewsletterLead) {
  const email = normalizeEmail(lead.email);
  const phone = lead.phone ? normalizePhone(lead.phone) : null;
  return {
    event: "generate_lead",
    lead_source: lead.source,
    ...(lead.gender ? { gender: lead.gender } : {}),
    ...(email ? { sha256_email_address: await sha256Hex(email) } : {}),
    ...(phone ? { sha256_phone_number: await sha256Hex(phone) } : {}),
  };
}

export async function trackNewsletterLead(lead: NewsletterLead): Promise<void> {
  try {
    if (typeof window === "undefined" || !hasCookieConsent()) return;

    const email = normalizeEmail(lead.email);
    if (email) {
      try {
        localStorage.setItem(FBE_EMAIL_STORAGE_KEY, email);
      } catch {
        // storage bloqueado -- o generate_lead abaixo ainda sai
      }
      await syncMetaUserData();
    }

    const event = await buildLeadEvent(lead);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  } catch (error) {
    console.error("[leadTracking] falha ao registrar lead:", error);
  }
}
